import { useState } from "react";
import axios from "axios";
import { API, getAuthHeader } from "@/App";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Loader2, ChevronDown, ChevronRight, FileSpreadsheet, CheckCircle2 } from "lucide-react";

const STATUS = {
  new: ["New", "bg-emerald-100 text-emerald-800"],
  changed: ["Changed", "bg-amber-100 text-amber-800"],
  fill: ["Fills missing info", "bg-sky-100 text-sky-800"],
  same: ["Same", "bg-slate-100 text-slate-600"],
  duplicate: ["Duplicate in book", "bg-rose-100 text-rose-700"],
};
const MODES = [
  ["skip", "Skip", "Keep the existing Database A data for parcels already there"],
  ["overwrite", "Overwrite", "Update only the changed fields; add any new products"],
  ["replace", "Replace", "Delete that parcel's old rows and load the sheet's rows (client code kept)"],
];
const FIELD = { product_added: "Product only in sheet", product_removed: "Product only in Database A", qty: "Qty" };

const Badge = ({ s, n }) => <span className={`inline-block rounded px-1.5 py-0.5 text-[11px] font-semibold ${STATUS[s][1]}`}>{n != null ? `${n} ` : ""}{STATUS[s][0]}</span>;

const ParcelList = ({ parcels }) => (
  <div className="max-h-72 overflow-y-auto divide-y divide-slate-100 bg-slate-50/60">
    {parcels.map(p => (
      <div key={p.key} className="px-3 py-1.5 text-xs" data-testid={`pb-parcel-${p.key}`}>
        <div className="flex flex-wrap items-center gap-2">
          <Badge s={p.status} />
          <span className="font-mono">{p.track_no || <i className="text-amber-700">no tracking</i>}</span>
          <span className="font-medium text-slate-800">{p.customer_name}</span>
          <span className="text-slate-500">{p.invoice_no} · {p.parcel_date} · {p.service} · {p.country} · {p.weight || 0}g{p.packer && ` · packer ${p.packer}`}</span>
        </div>
        <div className="text-slate-600 mt-0.5">{p.products.map(x => `${x.product} × ${x.qty}`).join(", ")}</div>
        {p.diffs.length > 0 && (
          <ul className="mt-1 space-y-0.5">
            {p.diffs.map((d, i) => (
              <li key={i} className={d.fill ? "text-sky-700" : "text-amber-800"}>
                {FIELD[d.field] || d.field}{d.product ? ` (${d.product})` : ""}: <s className="text-slate-400">{String(d.db ?? "—")}</s> → <b>{String(d.sheet ?? "—")}</b>
              </li>
            ))}
          </ul>
        )}
        {p.duplicate_of && <div className="text-rose-700">Same tracking already in tab “{p.duplicate_of}” — not imported twice</div>}
      </div>
    ))}
  </div>
);

const SheetRow = ({ s, picked, onPick, pid }) => {
  const [open, setOpen] = useState(false);
  const [parcels, setParcels] = useState(null);
  const toggle = async () => {
    setOpen(!open);
    if (!parcels) {
      const r = await axios.get(`${API}/database-a/packing-book/preview/${pid}`, { headers: getAuthHeader(), params: { sheet: s.sheet } });
      setParcels(r.data.parcels);
    }
  };
  return (
    <div className={`border-b border-slate-200 ${s.imported ? "bg-emerald-50/70" : s.has_orders ? "" : "opacity-60"}`} data-testid={`pb-sheet-${s.sheet}`}>
      <div className="flex items-center gap-2 px-2 py-1.5 text-xs">
        <input type="checkbox" checked={picked} onChange={onPick} disabled={!s.has_orders || s.imported} data-testid={`pb-pick-${s.sheet}`} />
        <button onClick={toggle} disabled={!s.has_orders} data-testid={`pb-sheet-toggle-${s.sheet}`} className="flex items-center gap-1 font-semibold text-slate-800 min-w-[150px] text-left">
          {open ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}{s.sheet}
        </button>
        <span className="text-slate-500 min-w-[150px] truncate" title={s.invoices.join(", ")}>{s.invoices.join(", ") || "no invoice"}</span>
        <span className="text-slate-600">{s.parcels} parcels · {s.rows} rows</span>
        <div className="flex flex-wrap gap-1 ml-auto">
          {["new", "changed", "fill", "same", "duplicate"].filter(k => s[k]).map(k => <Badge key={k} s={k} n={s[k]} />)}
          {s.no_tracking > 0 && <span className="rounded px-1.5 py-0.5 text-[11px] bg-orange-100 text-orange-800">{s.no_tracking} no tracking</span>}
          {s.skipped_rows > 0 && <span className="rounded px-1.5 py-0.5 text-[11px] bg-slate-100 text-slate-500">{s.skipped_rows} rows skipped</span>}
          {s.imported ? <span className="rounded px-1.5 py-0.5 text-[11px] font-bold bg-emerald-600 text-white" data-testid={`pb-imported-${s.sheet}`}>✓ Imported ({s.imported})</span>
            : !s.has_orders && <span className="text-[11px] text-slate-400">no orders — skipped</span>}
        </div>
      </div>
      {open && (parcels ? <ParcelList parcels={parcels} /> : <div className="p-3 text-center"><Loader2 className="w-4 h-4 animate-spin inline" /></div>)}
    </div>
  );
};

export const PackingBookImportDialog = ({ open, onOpenChange, onDone, initialFile }) => {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [prev, setPrev] = useState(null);
  const [picked, setPicked] = useState({});
  const [mode, setMode] = useState("");
  const [result, setResult] = useState(null);
  const f = file || initialFile;
  const reset = () => { setPrev(null); setPicked({}); setMode(""); setResult(null); };
  const analyse = async () => {
    if (!f) { toast.error("Pick the packing book first"); return; }
    setBusy(true); reset();
    try {
      const fd = new FormData(); fd.append("file", f);
      const r = await axios.post(`${API}/database-a/packing-book/preview`, fd, { headers: getAuthHeader() });
      setPrev(r.data);
      setPicked(Object.fromEntries(r.data.sheets.map(s => [s.sheet, s.has_orders])));
    } catch (e) { toast.error(e.response?.data?.detail || "Could not read the workbook"); }
    finally { setBusy(false); }
  };
  const chosen = prev ? prev.sheets.filter(s => picked[s.sheet]) : [];
  const sum = (k) => chosen.reduce((n, s) => n + s[k], 0);
  const commit = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/database-a/packing-book/commit`, { preview_id: prev.preview_id, sheets: chosen.map(s => s.sheet), mode }, { headers: getAuthHeader() });
      setResult(r.data);
      setPrev(p => ({ ...p, sheets: p.sheets.map(s => (picked[s.sheet] ? { ...s, imported: mode } : s)) }));
      setPicked({}); setMode("");
      setTimeout(() => document.querySelector('[data-testid="pb-result"]')?.scrollIntoView({ behavior: "smooth", block: "center" }), 50);
      toast.success(`Imported: ${r.data.new} new · ${r.data.overwritten} overwritten · ${r.data.replaced} replaced · ${r.data.skipped} skipped`);
      onDone && onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Import failed"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) { reset(); setFile(null); } onOpenChange(v); }}>
      <DialogContent className="max-w-6xl max-h-[92vh] overflow-y-auto" data-testid="pb-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><FileSpreadsheet className="w-5 h-5 text-emerald-600" />Import Packing Book → Database A</DialogTitle></DialogHeader>
        <p className="text-xs text-slate-500">Multi-tab packing list (e.g. AUGUST PACKING SHEET). Every tab and every invoice block in it is read; banners, HEIGHT/WIDTH/LENGTH and blank rows are skipped; the column after AMT is saved as packer. Nothing is written until you review and press Import.</p>
        <div className="flex flex-wrap items-center gap-2">
          <input type="file" accept=".xlsx" onChange={e => { setFile(e.target.files[0]); reset(); }} data-testid="pb-file" className="text-sm border border-slate-300 rounded-lg p-1.5" />
          {initialFile && !file && <span className="text-xs text-slate-600">{initialFile.name}</span>}
          <Button size="sm" onClick={analyse} disabled={busy || !f} data-testid="pb-analyse-btn">{busy && !prev ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}Analyse</Button>
        </div>
        {prev && (
          <>
            <div className="flex flex-wrap gap-2 text-xs" data-testid="pb-totals">
              <span className="font-semibold">{prev.sheets.length} tabs · {prev.totals.parcels} parcels · {prev.totals.rows} rows</span>
              {["new", "changed", "fill", "same", "duplicate"].map(k => <Badge key={k} s={k} n={prev.totals[k]} />)}
              <span className="rounded px-1.5 py-0.5 bg-orange-100 text-orange-800">{prev.totals.no_tracking} no tracking (imported without — add later)</span>
            </div>
            {busy && (
              <div className="rounded-lg border border-blue-200 bg-blue-50 p-3 text-sm text-blue-800 flex items-center gap-2" data-testid="pb-importing">
                <Loader2 className="w-4 h-4 animate-spin" /> Importing {chosen.length} tab(s) into Database A — please keep this window open…
              </div>
            )}
            {result && (
              <div className="rounded-lg border-2 border-emerald-400 bg-emerald-50 p-3 text-sm text-emerald-900 space-y-1" data-testid="pb-result">
                <div className="font-bold text-base flex items-center gap-1"><CheckCircle2 className="w-5 h-5" /> Import finished — saved to Database A ({result.mode})</div>
                <div>{result.new} new · {result.overwritten} overwritten · {result.replaced} replaced · {result.skipped} skipped · {result.same} unchanged</div>
                <div className="text-xs">{result.rows_inserted} rows added · {result.rows_updated} updated · {result.rows_deleted} old rows removed. Imported tabs are marked ✓ below. Undo: Upload Corrected → History.</div>
                <Button size="sm" variant="outline" onClick={() => { reset(); setFile(null); onOpenChange(false); }} data-testid="pb-close-after">Close & view Database A</Button>
              </div>
            )}
            <div className="flex gap-2 text-xs">
              <button className="underline text-blue-700" onClick={() => setPicked(Object.fromEntries(prev.sheets.map(s => [s.sheet, s.has_orders])))} data-testid="pb-select-all">Select all with orders</button>
              <button className="underline text-blue-700" onClick={() => setPicked({})} data-testid="pb-select-none">Clear</button>
            </div>
            <div className="border border-slate-200 rounded-lg max-h-[42vh] overflow-y-auto">
              {prev.sheets.map(s => <SheetRow key={s.sheet} s={s} pid={prev.preview_id} picked={!!picked[s.sheet]} onPick={() => setPicked(p => ({ ...p, [s.sheet]: !p[s.sheet] }))} />)}
            </div>
            <div className="rounded-lg border border-slate-200 p-3 space-y-2" data-testid="pb-mode">
              <div className="text-sm font-semibold text-slate-800">For the {sum("changed") + sum("fill")} parcel(s) already in Database A with differences — choose an action:</div>
              <div className="grid sm:grid-cols-3 gap-2">
                {MODES.map(([k, label, hint]) => (
                  <label key={k} className={`cursor-pointer rounded-lg border p-2 text-xs ${mode === k ? "border-blue-500 bg-blue-50" : "border-slate-200 hover:bg-slate-50"}`} data-testid={`pb-mode-${k}`}>
                    <input type="radio" name="pbmode" className="mr-1" checked={mode === k} onChange={() => setMode(k)} /><b>{label}</b>
                    <div className="text-slate-500 mt-0.5">{hint}</div>
                  </label>
                ))}
              </div>
              <div className="flex items-center gap-3">
                <Button onClick={commit} disabled={busy || !mode || chosen.length === 0} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="pb-import-btn">
                  {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}Import {chosen.length} tab(s) · {sum("new")} new parcels
                </Button>
                {!mode && <span className="text-xs text-amber-700">Pick Skip, Overwrite or Replace first</span>}
              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
